import React, { ErrorInfo } from "react";
import { Result, Button, Typography, notification } from "antd";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const { Paragraph, Text } = Typography;

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error in React component tree:", error, errorInfo);
  }

  componentDidMount() {
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection,
    );
    window.addEventListener("error", this.handleWindowError);
  }

  componentWillUnmount() {
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection,
    );
    window.removeEventListener("error", this.handleWindowError);
  }

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    console.error("Unhandled promise rejection:", event.reason);

    // Prevent generic notification if Axios interceptor already handled it
    if (
      event.reason?.isAxiosError &&
      (event.reason.response?.status === 401 ||
        event.reason.response?.status === 403 ||
        event.reason.response?.status >= 500)
    ) {
      return;
    }

    notification.error({
      message: "Unexpected Background Error",
      description:
        event.reason?.message || "An unexpected background error occurred.",
    });
  };

  handleWindowError = (event: ErrorEvent) => {
    console.error("Global window error:", event.error);

    // ResizeObserver loop limit exceeded is a harmless warning masquerading as an error
    if (event.message === "ResizeObserver loop limit exceeded") {
      return;
    }

    notification.error({
      message: "Unexpected Error",
      description: event.message || "An unexpected error occurred.",
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <Result
            status="500"
            title="Something went wrong"
            subTitle="The application encountered an unexpected error."
            extra={
              <Button type="primary" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
